import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { MapContainer, TileLayer, CircleMarker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { depotsAPI, companiesAPI } from '../services/api';
import DataTable from '../components/DataTable';
import { useAuthStore } from '../store/authStore';
import { hasAtLeastPermission } from '../utils/moduleAccess';

function DepotLocationPicker({ latitude, longitude, onChange }) {
  const center =
    typeof latitude === 'number' && typeof longitude === 'number'
      ? [latitude, longitude]
      : [0, 0];

  function LocationClickHandler() {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        onChange(lat, lng);
      },
    });
    return null;
  }

  return (
    <div className="form-group">
      <label>Location</label>
      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        Click on the map to set the depot location. This will be used on the
        global map to pinpoint the depot.
      </p>
      <div style={{ height: '220px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
        <MapContainer center={center} zoom={latitude && longitude ? 6 : 2} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {typeof latitude === 'number' && typeof longitude === 'number' && (
            <CircleMarker center={[latitude, longitude]} radius={8} color="#0077B6" fillColor="#0096d1" fillOpacity={0.8} />
          )}
          <LocationClickHandler />
        </MapContainer>
      </div>
      <div style={{ marginTop: '8px', fontSize: '12px', color: '#333' }}>
        Lat: {typeof latitude === 'number' ? latitude.toFixed(5) : 'Not set'} | Lng:{' '}
        {typeof longitude === 'number' ? longitude.toFixed(5) : 'Not set'}
      </div>
    </div>
  );
}

export default function Depots() {
  const { user: currentUser } = useAuthStore();
  const permissions = useAuthStore((state) => state.permissions);
  const isAdminLike = currentUser?.user_type === 'gods_eye' || currentUser?.user_type === 'manager';
  const [depots, setDepots] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    ruc: '',
    address: '',
    email: '',
    phone: '',
    latitude: null,
    longitude: null,
    company_id: currentUser?.company_id || 1,
  });

  useEffect(() => {
    fetchDepots();
    fetchCompanies();
  }, []);

  const fetchDepots = async () => {
    setLoading(true);
    try {
      const response = await depotsAPI.getAll();
      setDepots(response.data);
    } catch (error) {
      toast.error('Error fetching depots');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      // Use public endpoint for non-gods_eye users (cliente only has access to their own company)
      const response = currentUser?.user_type === 'gods_eye' 
        ? await companiesAPI.getAll()
        : await companiesAPI.getPublic();
      setCompanies(response.data);
      // Use current user's company, or first company if available
      const defaultCompanyId = currentUser?.company_id || (response.data.length > 0 ? response.data[0].id : 1);
      setFormData(prev => ({ 
        ...prev,
        company_id: defaultCompanyId,
      }));
    } catch (error) {
      console.error('Error fetching companies', error);
      toast.error('Error loading companies');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'depots', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to create or edit depots');
      return;
    }
    try {
      // Validate company_id is set
      if (!formData.company_id || formData.company_id === 0) {
        toast.error('Please select a company');
        return;
      }
      
      if (editingId) {
        console.log('Updating depot with payload:', formData);
        await depotsAPI.update(editingId, formData);
        toast.success('Depot updated successfully');
        setEditingId(null);
      } else {
        console.log('Creating depot with payload:', formData);
        await depotsAPI.create(formData);
        toast.success('Depot created successfully');
      }
      const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
      setFormData({ 
        name: '', 
        ruc: '', 
        address: '', 
        email: '', 
        phone: '', 
        latitude: null,
        longitude: null,
        company_id: defaultCompanyId 
      });
      setShowForm(false);
      fetchDepots();
    } catch (error) {
      console.error('Submit error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Error saving depot';

      if (Array.isArray(detail)) {
        // Pydantic-style validation errors
        errorMsg = detail
          .slice(0, 3)
          .map((err) => {
            if (err.loc && err.msg) {
              return `${err.loc.join('.')}: ${err.msg}`;
            }
            if (err.msg) return err.msg;
            return JSON.stringify(err);
          })
          .join('\n');
        if (detail.length > 3) {
          errorMsg += '\n...';
        }
      } else if (detail && typeof detail === 'string') {
        errorMsg = detail;
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    const canDelete = isAdminLike || hasAtLeastPermission(permissions, 'depots', 'full_access');
    if (!canDelete) {
      toast.error('You do not have permission to delete depots');
      return;
    }
    if (window.confirm('Are you sure you want to delete this depot?')) {
      try {
        await depotsAPI.delete(id);
        toast.success('Depot deleted successfully');
        fetchDepots();
      } catch (error) {
        toast.error('Error deleting depot');
      }
    }
  };

  const handleEdit = (depot) => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'depots', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to edit depots');
      return;
    }
    const companyId = depot.company_id || currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({ 
      ...depot,
      company_id: companyId
    });
    setEditingId(depot.id);
    setShowForm(true);
  };

  const handleAddClick = () => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'depots', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to create depots');
      return;
    }
    const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({ 
      name: '', 
      ruc: '', 
      address: '', 
      email: '', 
      phone: '', 
      latitude: null,
      longitude: null,
      company_id: defaultCompanyId 
    });
    setEditingId(null);
    setShowForm(true);
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company #${companyId}`;
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'ruc', label: 'RUC', sortable: true },
    { 
      key: 'location', 
      label: 'Location', 
      render: (value, row) =>
        row.latitude && row.longitude
          ? `${row.latitude.toFixed(4)}, ${row.longitude.toFixed(4)}`
          : 'Not set',
      sortable: false,
    },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
    ...(currentUser?.user_type !== 'cliente' ? [{
      key: 'company_id',
      label: 'Company',
      render: (value) => getCompanyName(value),
      sortable: true
    }] : []),
  ];

  return (
    <div className="container">
      <h1 className="page-title">Depots</h1>
      {(isAdminLike || hasAtLeastPermission(permissions, 'depots', 'edit')) && (
        <button className="btn btn-primary" onClick={handleAddClick}>
          Add Depot
        </button>
      )}

      {showForm && (
        <div className="modal active">
          <div className="modal-content">
            <span className="close" onClick={() => { 
              setShowForm(false); 
              setEditingId(null);
              setFormData({ name: '', ruc: '', address: '', email: '', phone: '', latitude: null, longitude: null, company_id: currentUser?.company_id || 1 });
            }}>&times;</span>
            <h2>{editingId ? 'Edit Depot' : 'Add Depot'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>RUC</label>
                <input
                  type="text"
                  value={formData.ruc}
                  onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <DepotLocationPicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onChange={(lat, lng) => setFormData({ ...formData, latitude: lat, longitude: lng })}
              />
              <div className="form-group">
                <label>Company</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                  disabled={currentUser?.user_type !== 'gods_eye'}
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {currentUser?.user_type !== 'gods_eye' && (
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Your company is automatically selected
                  </small>
                )}
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          data={depots}
          columns={columns}
          searchableFields={['name', 'ruc', 'email', 'phone']}
          itemsPerPage={10}
          onEdit={handleEdit}
          onDelete={(depot) => handleDelete(depot.id)}
          title="Depots List"
        />
      )}
    </div>
  );
}
